import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessagePropType } from '../../../../../prop-types/intl';
import { CookieService } from '../../../../../services/cookie-service';
import { Button } from 'ui-components/components/button';
import Global from '../../../../../store/global-selectors';
import { reactivateSubscriptionWithCoupon } from '../../../api/subscription';
import { refreshSellerPlanAndSubscriptionInfo, updateSellerPlanInfo } from '../../../../../store/seller/seller.actions';
import { CANCELLED } from '../../constants/account-issues';
import { CHURN_WIN_BACK_COOKIE_KEY, showErrorNotification } from './helpers';
import { CHURN_WIN_BACK_ACTIONS, useTrackChurnWinBackAction } from './event-tracking/use-track-churn-win-back-action';

const ChurnWinBackButton = ({ buttonMessage, coupon, status }) => {
  const dispatch = useDispatch();
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);

  const { suggestedPlan } = subscriptionData || {};
  const { planCode: suggestedPlanCode } = suggestedPlan;
  const { plan } = planInfo;

  const trackApplyAction = useTrackChurnWinBackAction(CHURN_WIN_BACK_ACTIONS.COUPON_APPLIED, { coupon });
  const eventPayload = useMemo(() => ({ coupon }), [coupon]);
  const trackError = useTrackChurnWinBackAction(CHURN_WIN_BACK_ACTIONS.ERROR, eventPayload);

  const onClick = useCallback(async () => {
    trackApplyAction();
    try {
      const response = await reactivateSubscriptionWithCoupon({
        couponName: coupon,
        isNewSubscription: status === CANCELLED,
        planCode: suggestedPlanCode || plan.code,
        additionalStores: 0,
        // TODO: this seems pretty arbitrary and a dark pattern??? Take a look in cancellation context cleanup. https://auctane.atlassian.net/browse/SPD-18223
        additionalUsers: plan.userLimit.limit - 1,
      });
      const updatedPlanInfo = response?.data?.planInfo;
      dispatch(updateSellerPlanInfo(updatedPlanInfo));
      CookieService.removeCookie(CHURN_WIN_BACK_COOKIE_KEY);
    } catch (e) {
      trackError(e);
      showErrorNotification(e);
      dispatch(refreshSellerPlanAndSubscriptionInfo());
    }
  }, [trackApplyAction, coupon, status, suggestedPlanCode, plan.code, plan.userLimit.limit, trackError, dispatch]);

  return (
    <Button onClick={onClick} theme='primary'>
      {buttonMessage}
    </Button>
  );
};

ChurnWinBackButton.propTypes = {
  coupon: PropTypes.string.isRequired,
  buttonMessage: FormattedMessagePropType.isRequired,
  status: PropTypes.string.isRequired,
};

export default ChurnWinBackButton;
